import * as React from 'react';
import { useLexicalComposerContext } from '@fluentui-copilot/react-text-editor';
import { useIsomorphicLayoutEffect } from '@fluentui/react-components';
import type { IBasicFunctionalityBase } from '@fluentui-copilot/chat-input-plugins';
import { BasicFunctionalityBase } from '@fluentui-copilot/chat-input-plugins';

export type BasicFunctionalityPluginProps = {
  // Whether or not to clear the input when only whitespace is remaining
  trimWhitespace?: boolean;
  onCountChanged?: (count: number) => void;
  onContentChange?: (value: string) => void;
  onPaste?: (event: ClipboardEvent) => void;
  isSentinelNodeEnabled?: boolean;
};

export type BasicFunctionalityPluginRef = {
  setIsDisabled: (isDisabled: boolean) => void;
};

export const BasicFunctionalityPlugin = React.forwardRef<BasicFunctionalityPluginRef, BasicFunctionalityPluginProps>(
  (props, ref) => {
    const { trimWhitespace, onCountChanged, onContentChange, onPaste, isSentinelNodeEnabled } = props;

    const [editor] = useLexicalComposerContext();

    const [basicFunctionalityImpl, setBasicFunctionalityImpl] = React.useState<IBasicFunctionalityBase | null>(null);

    useIsomorphicLayoutEffect(() => {
      setBasicFunctionalityImpl(new BasicFunctionalityBase(editor, isSentinelNodeEnabled));

      return () => {
        setBasicFunctionalityImpl(prev => {
          prev?.cleanup();
          return null;
        });
      };
    }, [editor, isSentinelNodeEnabled]);

    React.useImperativeHandle<BasicFunctionalityPluginRef, BasicFunctionalityPluginRef>(
      ref,
      () => ({
        setIsDisabled(isDisabled: boolean) {
          basicFunctionalityImpl?.setIsDisabled(isDisabled);
        },
      }),
      [basicFunctionalityImpl],
    );

    React.useEffect(() => {
      basicFunctionalityImpl?.activateContentCallbacks(onContentChange, onCountChanged);
      return () => basicFunctionalityImpl?.deactivateContentCallbacks();
    }, [basicFunctionalityImpl, editor, onContentChange, onCountChanged]);

    React.useEffect(() => {
      if (onPaste) {
        basicFunctionalityImpl?.activatePasteCallback(onPaste);
      }
      return () => basicFunctionalityImpl?.deactivatePasteCallback();
    }, [basicFunctionalityImpl, editor, onPaste]);

    React.useEffect(() => {
      if (trimWhitespace) {
        basicFunctionalityImpl?.activateTrimWhitespace();
      }
      return () => basicFunctionalityImpl?.deactivateTrimWhitespace();
    }, [basicFunctionalityImpl, editor, trimWhitespace]);

    return null;
  },
);
